import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';
import {
    formatLosslessNumberFixed,
    isLosslessNumber,
    type LosslessNumber,
    runInBackground,
} from '@pillar-portal/api-types';

const MonetaryAmountSchema = z.union([z.number(), z.string()]);

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export { runInBackground };

export function formatMonetaryAmount(
    amount: string | number | LosslessNumber,
    currencyCode: string = 'USD'
): string {
    if (isLosslessNumber(amount)) {
        const fixedAmount = formatLosslessNumberFixed(amount, 2);
        const negative = fixedAmount.startsWith('-');
        const unsignedAmount = negative ? fixedAmount.slice(1) : fixedAmount;
        const [whole = '0', fraction = '00'] = unsignedAmount.split('.');
        const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/gu, ',');
        const formattedAmount = `${negative ? '-' : ''}${groupedWhole}.${fraction}`;
        return currencyCode === 'USD'
            ? `$${formattedAmount}`
            : `${formattedAmount} ${currencyCode}`;
    }
    const parseResult = MonetaryAmountSchema.safeParse(amount);
    if (!parseResult.success) {
        throw new Error(`Invalid monetary amount: ${String(amount)}`);
    }
    const numericAmount: number = Number(parseResult.data);
    if (!Number.isFinite(numericAmount)) {
        throw new Error(`Invalid monetary amount: ${String(amount)}`);
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
    }).format(numericAmount);
}
